import supabase from "../../supabase/supabase";

const TABLE = "documents";
const BUCKET = "client-documents";
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MIME_BY_EXTENSION = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};
const CATEGORIES = new Set([
  "general",
  "tax",
  "bank_statement",
  "financial_statement",
  "contract",
  "invoice",
  "receipt",
  "payroll",
  "legal",
  "other",
]);
const VISIBILITIES = new Set([
  "client_only",
  "shared_with_accountant",
]);

function requireOrganization(organizationId) {
  if (!organizationId) {
    throw new Error("Select a business to manage its documents.");
  }
}

function check({ data, error }) {
  if (error) throw error;
  return data;
}

function safeFilename(fileName) {
  const name = String(fileName || "document")
    .normalize("NFKD")
    .replace(/[^\w.-]+/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^\.+/, "")
    .slice(0, 180);
  return name || "document";
}

function getFileType(file) {
  const extension = String(file.name || "").split(".").pop().toLowerCase();
  const expectedMime = MIME_BY_EXTENSION[extension];
  if (!expectedMime) {
    throw new Error("Supported files are PDF, JPG, PNG, WebP, DOCX, and XLSX.");
  }
  if (file.type && file.type !== expectedMime) {
    const compatibleImage = extension === "jpg" && file.type === "image/pjpeg";
    if (!compatibleImage) {
      throw new Error("The selected file type does not match its extension.");
    }
  }
  return expectedMime;
}

function validateFile(file) {
  if (!file) throw new Error("Choose a file to upload.");
  if (file.size > MAX_FILE_SIZE) {
    throw new Error("Files must be 20 MB or smaller.");
  }
  return getFileType(file);
}

function scopedDocuments() {
  return supabase.schema("accounting").from(TABLE);
}

export const clientDocumentService = {
  async getClientDocuments({
    organizationId,
    page = 1,
    pageSize = 15,
    search = "",
    category = "all",
    visibility = "all",
    sortDirection = "desc",
  }) {
    requireOrganization(organizationId);

    let query = scopedDocuments()
      .select("*", { count: "exact" })
      .eq("organization_id", organizationId)
      .eq("status", "active");

    if (search.trim()) {
      query = query.ilike("file_name", `%${search.trim()}%`);
    }
    if (category !== "all") query = query.eq("category", category);
    if (visibility !== "all") query = query.eq("visibility", visibility);

    const ascending = sortDirection === "asc";
    const from = (Math.max(1, page) - 1) * pageSize;
    const result = check(
      await query
        .order("created_at", { ascending })
        .order("id", { ascending })
        .range(from, from + pageSize - 1)
    );

    return {
      documents: result || [],
      totalCount: result?.count || 0,
    };
  },

  async getClientDocumentSummary({ organizationId }) {
    requireOrganization(organizationId);

    const rows = check(
      await scopedDocuments()
        .select("id,visibility,created_at,status")
        .eq("organization_id", organizationId)
        .eq("status", "active")
    ) || [];

    const recentCutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return rows.reduce((summary, row) => {
      summary.total += 1;
      if (row.visibility === "shared_with_accountant") summary.shared += 1;
      if (row.visibility === "client_only") summary.private += 1;
      if (new Date(row.created_at).getTime() >= recentCutoff) summary.recent += 1;
      return summary;
    }, { total: 0, shared: 0, private: 0, recent: 0 });
  },

  async getClientDocumentDetails({ organizationId, documentId }) {
    requireOrganization(organizationId);
    if (!documentId) throw new Error("Document ID is required.");

    return check(
      await scopedDocuments()
        .select("*")
        .eq("organization_id", organizationId)
        .eq("id", documentId)
        .single()
    );
  },

  async uploadClientDocument({
    organizationId,
    file,
    category = "general",
    description = "",
    visibility = "shared_with_accountant",
  }) {
    requireOrganization(organizationId);
    if (!CATEGORIES.has(category)) throw new Error("Choose a supported document category.");
    if (!VISIBILITIES.has(visibility)) throw new Error("Choose a supported visibility option.");
    const fileType = validateFile(file);
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!authData?.user?.id) throw new Error("Sign in again before uploading documents.");

    const documentId = crypto.randomUUID();
    const fileName = safeFilename(file.name);
    const storagePath = `${organizationId}/${documentId}/${fileName}`;
    const metadata = {
      id: documentId,
      organization_id: organizationId,
      uploaded_by: authData.user.id,
      file_name: file.name,
      storage_path: storagePath,
      file_type: fileType,
      file_size: file.size,
      category,
      description: description.trim(),
      visibility,
      status: "active",
    };

    check(await scopedDocuments().insert(metadata));

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(storagePath, file, {
        contentType: fileType,
        upsert: false,
      });

    if (uploadError) {
      const { error: cleanupError } = await supabase
        .schema("accounting")
        .rpc("cleanup_failed_document_upload", {
          p_document_id: documentId,
        });

      if (cleanupError) {
        throw new Error(
          `Upload failed and cleanup also failed. Document ID ${documentId} needs administrative review.`
        );
      }
      throw new Error(uploadError.message || "Document upload failed.");
    }

    return metadata;
  },

  async getClientDocumentPreviewUrl({ organizationId, document }) {
    requireOrganization(organizationId);
    if (!document?.id) throw new Error("Document is unavailable.");
    const scoped = await this.getClientDocumentDetails({
      organizationId,
      documentId: document.id,
    });
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(scoped.storage_path, 60);
    if (error) throw error;
    return data.signedUrl;
  },

  async downloadClientDocument({ organizationId, document }) {
    requireOrganization(organizationId);
    if (!document?.id) throw new Error("Document is unavailable.");
    const scoped = await this.getClientDocumentDetails({
      organizationId,
      documentId: document.id,
    });
    return check(
      await supabase.storage
        .from(BUCKET)
        .download(scoped.storage_path)
    );
  },

  async updateClientDocument({ organizationId, documentId, updates }) {
    requireOrganization(organizationId);
    if (!documentId) throw new Error("Document ID is required.");
    const safeUpdates = {
      category: updates.category,
      description: String(updates.description || "").trim(),
      visibility: updates.visibility,
      updated_at: new Date().toISOString(),
    };
    if (!CATEGORIES.has(safeUpdates.category)) {
      throw new Error("Choose a supported document category.");
    }
    if (!VISIBILITIES.has(safeUpdates.visibility)) {
      throw new Error("Choose a supported visibility option.");
    }

    return check(
      await scopedDocuments()
        .update(safeUpdates)
        .eq("organization_id", organizationId)
        .eq("id", documentId)
        .select("*")
        .single()
    );
  },

  async archiveClientDocument({ organizationId, documentId }) {
    requireOrganization(organizationId);
    if (!documentId) throw new Error("Document ID is required.");

    return check(
      await scopedDocuments()
        .update({
          status: "archived",
          updated_at: new Date().toISOString(),
        })
        .eq("organization_id", organizationId)
        .eq("id", documentId)
        .eq("status", "active")
        .select("id,status")
        .single()
    );
  },
};

export { MAX_FILE_SIZE, CATEGORIES, VISIBILITIES };